import React, {
  useCallback, useContext, useEffect, useState,
} from 'react'
import {
  Button, Dropdown, OverlayTrigger, Popover,
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useTranslation, getI18n } from 'react-i18next'
import {
  DateRangePicker, RangeKeyDict, defaultInputRanges, createStaticRanges, StaticRange, InputRange,
} from 'react-date-range'
import { enGB } from 'date-fns/locale'
import { DebounceInput } from 'react-debounce-input'
import subMonths from 'date-fns/subMonths'
import addYears from 'date-fns/addYears'
import subYears from 'date-fns/subYears'
import addDays from 'date-fns/addDays'
import endOfDay from 'date-fns/endOfDay'
import startOfDay from 'date-fns/startOfDay'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import addMonths from 'date-fns/addMonths'
import startOfISOWeek from 'date-fns/startOfISOWeek'
import endOfISOWeek from 'date-fns/endOfISOWeek'
import startOfYear from 'date-fns/startOfYear'
import endOfYear from 'date-fns/endOfYear'
import { TableFilter } from 'types/PageResponse'
import { UserContext } from 'shared/users/contexts/UserContext'
import { COMMODITY_CODE_MAX_LENGTH } from 'shared/ncts/common/constants'
import useCodelist, { createDepartureCustomsOfficesBySupportedCountriesAndUserFilter } from 'features/declaration/codelist/api/useCodelist'
import SerbianInboundFetchButton from 'features/serbia-inbound-fetch/components/SerbianInboundFetchButton'
import TransitsTable from './table/Table'
import ROUTES from '../../../config/routes'
import { TitleBar } from '../../../components/TitleBar'
import Export from '../../Export'
import useUserPreference from './hooks/useUserPreference'
import usePrefetchCodelist from './hooks/usePrefetchCodelist/usePrefetchCodelist'
import NotesModal from '../common/components/NotesModal/NotesModal'
import TableColumnPreferencesModal from './components/TableColumnPreferencesModal'
import TransitPreview from '../../../features/declaration/preview/components'

interface CreateDeclarationRoute {
  name: string
  path: string
}

const newDeclarationRoutes: CreateDeclarationRoute[] = [
  { name: 'T1', path: ROUTES.newTransitP5 },
  { name: 'ENS', path: ROUTES.newEns },
]

const getStaticRanges: () => StaticRange[] = () => {
  const currentDate = new Date()

  return [
    {
      label: getI18n().t('datepicker.today'),
      isSelected: () => false,
      range: () => ({
        startDate: startOfDay(currentDate),
        endDate: endOfDay(currentDate),
      }),
    },
    {
      label: getI18n().t('datepicker.week', { context: 'this' }),
      isSelected: () => false,
      range: () => ({
        startDate: startOfISOWeek(currentDate),
        endDate: endOfISOWeek(currentDate),
      }),
    },
    {
      label: getI18n().t('datepicker.week', { context: 'last' }),
      isSelected: () => false,
      range: () => ({
        startDate: startOfISOWeek(addDays(currentDate, -7)),
        endDate: endOfISOWeek(addDays(currentDate, -7)),
      }),
    },
    {
      label: getI18n().t('datepicker.month', { context: 'this' }),
      isSelected: () => false,
      range: () => ({
        startDate: startOfMonth(currentDate),
        endDate: endOfMonth(currentDate),
      }),
    },
    {
      label: getI18n().t('datepicker.month', { context: 'last' }),
      isSelected: () => false,
      range: () => ({
        startDate: startOfMonth(addMonths(currentDate, -1)),
        endDate: endOfMonth(addMonths(currentDate, -1)),
      }),
    },
    {
      label: getI18n().t('datepicker.year', { context: 'this' }),
      isSelected: () => false,
      range: () => ({
        startDate: startOfYear(currentDate),
        endDate: endOfYear(currentDate),
      }),
    },
    {
      label: getI18n().t('datepicker.year', { context: 'last' }),
      isSelected: () => false,
      range: () => ({
        startDate: startOfYear(addYears(currentDate, -1)),
        endDate: endOfYear(addYears(currentDate, -1)),
      }),
    },
  ]
}
const createCustomInputRange = (): InputRange[] => {
  const defaultInputRange = defaultInputRanges.find((r) => r.label === 'days up to today')
  return defaultInputRange !== undefined ? [{
    ...defaultInputRange,
    label: getI18n().t('datepicker.today', { context: 'upto' }),
  }] : []
}

const ALLOWED_DATE_RANGE_AMOUNT = 2

/* eslint-disable  @typescript-eslint/no-explicit-any */
function TransitOperations() {
  const {
    user,
  } = useContext(UserContext)

  const [endDate, setEndDate] = useState<Date>(new Date())
  const [startDate, setStartDate] = useState<Date>(() => subMonths(endDate, 3))
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [filters, setFilters] = useState<Array<TableFilter>>([])
  const [commodityCodeFilter, setCommodityCodeFilter] = useState<string>('')

  const [originalRow, setOriginalRow] = useState<any>()
  const [isNotesOpen, setIsNotesOpen] = useState<boolean>(false)
  const [transitOperationId, setTransitOperationId] = useState<number | null>(null)
  const [isExportModalVisible, setIsExportModalVisible] = useState<boolean>(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false)
  const [isTransitSidebarOpen, setIsTransitSidebarOpen] = useState(false)
  const [isDateSelectOpen, setIsDateSelectOpen] = useState<boolean>(false)
  const [customsOffices] = useCodelist('DESTINATION_CUSTOMS_OFFICES')

  const departureCustomOfficesFilterFn = useCallback(() => createDepartureCustomsOfficesBySupportedCountriesAndUserFilter(user), [user])
  const [departureCustomOffices] = useCodelist('DEPARTURE_CUSTOMS_OFFICES', departureCustomOfficesFilterFn)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { prefetchCodelists } = usePrefetchCodelist()
  const { userPreferences, setUserPreferences, isPreferencesLoading } = useUserPreference()
  const selectedDateString = `${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()} - ${
    endDate.getDate()}.${endDate.getMonth() + 1}.${endDate.getFullYear()}`

  const [staticRanges] = useState<StaticRange[]>(createStaticRanges(getStaticRanges()))
  const [inputRanges] = useState<InputRange[]>(createCustomInputRange())

  const toggleTransitSidebarVisibility = () => {
    setIsTransitSidebarOpen(!isTransitSidebarOpen)
  }
  const toggleDateSelectVisibility = () => {
    setIsDateSelectOpen(!isDateSelectOpen)
  }

  useEffect(() => {
    prefetchCodelists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeAside = (input: boolean, transitOperation?: number, tableRow?: unknown) => {
    setTransitOperationId(transitOperation || null)
    if (tableRow) {
      setOriginalRow(tableRow)
    }
    toggleTransitSidebarVisibility()
  }

  const toggleNotesModal = (input: boolean, transitOperation: number, tableRow?: unknown) => {
    setTransitOperationId(transitOperation || null)

    if (tableRow) {
      setOriginalRow(tableRow)
    }

    return setIsNotesOpen(input)
  }

  const isWithinAllowedRange = (start: Date, end: Date) => end <= addYears(start, ALLOWED_DATE_RANGE_AMOUNT)

  const handleSelect = ({ selection }: RangeKeyDict) => {
    if (!selection) return

    const end = selection.endDate ?? endDate
    let start = selection.startDate ?? startDate

    if (!isWithinAllowedRange(start, end)) {
      start = subYears(end, ALLOWED_DATE_RANGE_AMOUNT)
    }

    setStartDate(start)
    setEndDate(end)
  }

  const selectionRange = {
    startDate,
    endDate,
    key: 'selection',
  }

  const clearFilters = () => {
    const now = new Date()
    setEndDate(now)
    setStartDate(subMonths(now, 3))
    setStatusFilter('')
    setCommodityCodeFilter('')
    setFilters([])
    localStorage.setItem('search', JSON.stringify([]))
  }
  const popover = (
    <Popover
      id="DateRangePickerPopover"
      placement="top-end"
    >
      <DateRangePicker
        locale={enGB}
        ranges={[selectionRange]}
        onChange={handleSelect}
        inputRanges={inputRanges}
        staticRanges={staticRanges}
      />
    </Popover>
  )

  return (
    <>
      <TitleBar title={t('buttons.transits')}>
        <div
          className="d-flex justify-content-between ps-3 w-100 "
        >
          <div className="d-flex flex-column">
            <div className="d-flex">
              <OverlayTrigger
                trigger="click"
                placement="auto"
                overlay={popover}
                show={isDateSelectOpen}
              >
                <button
                  type="button"
                  className="btn btn-light btn-lg d-flex align-items-center rounded-lg datePicker"
                  onClick={toggleDateSelectVisibility}
                >
                  <i className="fal fa-calendar-alt fa-md me-sm-2" />
                  <span className="d-none d-sm-block">
                    {selectedDateString}
                  </span>
                </button>
              </OverlayTrigger>

              <Button
                className="mx-2"
                variant="outline-dark"
                size="sm"
                onClick={clearFilters}
              >
                <span>
                  {t('buttons.filter', { context: 'clear' })}
                </span>
              </Button>
              <DebounceInput
                className="form-control"
                type="search"
                debounceTimeout={900}
                maxLength={COMMODITY_CODE_MAX_LENGTH}
                placeholder={t('goodsItem.hsCode')}
                value={commodityCodeFilter}
                onChange={(event) => setCommodityCodeFilter(event.target.value)}
              />
              <SerbianInboundFetchButton />
            </div>
          </div>
          <div className="d-flex align-items-start">
            <button
              type="button"
              className="btn btn-lg me-4"
              disabled={isPreferencesLoading || userPreferences.length === 0}
              onClick={(() => {
                setIsFilterModalOpen(true)
              })}
            >
              <i className="far fa-filter me-2" />
              <span>
                {t('buttons.filter')}
              </span>
            </button>
            <div className="d-md-block d-xl-none d-lg-none mt-2">
              <Dropdown>
                <Dropdown.Toggle variant="link" className="p-0 hide-caret" id="dropdown-basic">
                  <i className="fas fa-ellipsis-h fa-md" />
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow-sm">
                  <Dropdown.Item onClick={() => setIsExportModalVisible(true)}>
                    <i className="far fa-file-excel me-2" />
                    <span>{t('buttons.export')}</span>
                  </Dropdown.Item>
                  {newDeclarationRoutes.map((route) => (
                    <Dropdown.Item
                      key={route.path}
                      onClick={() => {
                        navigate(`${route.path}`)
                      }}
                    >
                      {route.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <button
              type="button"
              className="btn btn-outline-dark btn-lg me-4 d-none d-lg-block"
              onClick={(() => {
                setIsExportModalVisible(true)
              })}
            >
              <i className="far fa-file-excel me-2" />
              <span>
                {t('buttons.export')}
              </span>
            </button>
            <div className="d-none d-lg-block">
              <Dropdown>
                <Dropdown.Toggle split variant="secondary" size="lg" id="new-transit-dropdown" className="text-primary">
                  <i className="far fa-plus me-2" />
                  <span className="pe-1">{t('buttons.addNew')}</span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow mw-20">
                  {newDeclarationRoutes.map((route) => (
                    <Dropdown.Item
                      key={route.path}
                      onClick={() => {
                        navigate(`${route.path}`)
                      }}
                    >
                      {route.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

          </div>
        </div>
      </TitleBar>
      <TransitsTable
        asideAction={changeAside}
        modalAction={toggleNotesModal}
        customsOffices={customsOffices}
        departureCustomOffices={departureCustomOffices}
        endDate={endDate}
        startDate={startDate}
        tablePreferences={userPreferences}
        commodityCodeFilter={commodityCodeFilter}
        setCommodityCodeFilter={setCommodityCodeFilter}
        filterState={{
          filters,
          setFilters,
        }}
        statusFilterState={{
          statusFilter,
          setStatusFilter,
        }}
      />
      {
        isDateSelectOpen
        && (
          <div
            className="active overlay"
            role="presentation"
            onClick={toggleDateSelectVisibility}
          />
        )
      }

      {(transitOperationId && isNotesOpen) && (
        <NotesModal
          originalRow={originalRow}
          setIsNotesModalOpen={setIsNotesOpen}
          isNotesModalOpen={isNotesOpen}
          transitOperationId={transitOperationId}
        />
      )}
      <Export
        modalVisibilityState={isExportModalVisible}
        setModalVisibility={setIsExportModalVisible}
        isPhaseFive
      />
      <TableColumnPreferencesModal
        isTableFilterModalOpen={isFilterModalOpen}
        setIsTableFilterModalOpen={setIsFilterModalOpen}
        setUserPreferences={setUserPreferences}
        userPreferences={userPreferences}
        isPreferencesLoading={isPreferencesLoading}
      />
      {
        transitOperationId !== null && (
          <TransitPreview
            isVisible={isTransitSidebarOpen}
            toggleVisibility={toggleTransitSidebarVisibility}
            transitOperationId={transitOperationId}
            originalRow={originalRow}
          />
        )
      }
    </>
  )
}

export default TransitOperations
